import type { Ref } from 'react'
import Link from 'next/link'
import { Image } from '@educator/components/Image'
import { useTranslation } from '@mc/i18n'
import { TrackClick } from '../../../../tracking/educatorTracking'
import type { SubjectHeroData } from '../../../../types/subjects'
import type { HeroStripConfig } from '../../../../utils/deriveHeroStrip'
import { applyEnrollmentStateDetails } from '../../utils/applyEnrollmentStateDetails'
import { CtaBox } from './CtaBox'

interface SubjectHeroSectionProps {
  data: SubjectHeroData
  ctaLabel?: string
  ctaHref?: string
  heroStrip?: HeroStripConfig
  mobileCtaButtonRef?: Ref<HTMLDivElement>
  desktopCtaButtonRef?: Ref<HTMLDivElement>
}

export const SubjectHeroSection = ({
  data,
  ctaLabel: ctaLabelOverride,
  ctaHref: ctaHrefOverride,
  heroStrip,
  mobileCtaButtonRef,
  desktopCtaButtonRef,
}: SubjectHeroSectionProps) => {
  const {
    categoryLabel,
    headline,
    artImageUrl,
    promoText,
    details,
    partnerLogoAlt,
    finePrint,
  } = data
  const { t } = useTranslation('@mc/educator')
  const ctaLabel = ctaLabelOverride ?? data.ctaLabel
  const ctaHref = ctaHrefOverride ?? data.ctaHref
  const effectiveDetails = applyEnrollmentStateDetails({
    details,
    heroStrip,
    t,
  })

  const detailPairs = effectiveDetails.reduce<(typeof effectiveDetails)[]>(
    (acc, item, i) => {
      if (i % 2 === 0) acc.push([item])
      else acc[acc.length - 1].push(item)
      return acc
    },
    [],
  )

  return (
    <section data-section='hero' className='w-full bg-black p-5'>
      <div className='mx-auto w-full max-w-[1152px]'>
        <div className='relative h-[512px] w-full overflow-hidden bg-edu-bg-tertiary md:h-[640px]'>
          <Image
            src={artImageUrl}
            alt={headline}
            fill
            preload
            fetchPriority='high'
            className='object-cover'
          />

          <div
            className='absolute inset-x-0 bottom-0 flex w-full justify-center'
            style={{ zIndex: 2 }}
          >
            <div className='flex w-full flex-col gap-6 xl:gap-25 px-6 lg:px-0 pb-6 md:max-w-[960px] md:flex-row md:items-start md:pb-18'>
              <div className='flex flex-1 gap-4 flex-col text-white md:max-w-[704px]'>
                <span className='text-body-2 font-semibold leading-body-2'>
                  {categoryLabel}
                </span>
                <div className='flex flex-col gap-5'>
                  <h1 className='m-0 font-serif text-headline-3 font-medium leading-headline-3 md:text-headline-2 md:leading-[66px]'>
                    {headline}
                  </h1>
                  <div
                    ref={desktopCtaButtonRef}
                    className='hidden md:block md:self-start'
                  >
                    <TrackClick
                      event='educatorPurchaseClick'
                      cta_location='hero'
                      cta={ctaLabel}
                    >
                      <TrackClick
                        event='educatorSignClick'
                        action={ctaLabel.toLowerCase()}
                        location='hero'
                      >
                        <Link
                          href={ctaHref}
                          className='hidden self-start bg-edu-accent-indigo px-5 py-4 text-body-1 leading-none font-medium transition-colors hover:bg-edu-accent-indigo-dark md:flex'
                        >
                          {ctaLabel}
                        </Link>
                      </TrackClick>
                    </TrackClick>
                  </div>
                  {finePrint && (
                    <p className='m-0 text-body-3 leading-body-3'>
                      {finePrint.text}{' '}
                      <Link href={finePrint.linkHref} className='underline!'>
                        {finePrint.linkText}
                      </Link>
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CtaBox
        ctaHref={ctaHref}
        ctaLabel={ctaLabel}
        promoText={promoText}
        detailPairs={detailPairs}
        partnerLogoAlt={partnerLogoAlt}
        ctaButtonRef={mobileCtaButtonRef}
      />
    </section>
  )
}
